import React from 'react';

interface ClaimBreakdownProps {
  step1_variable_profit_loss: number;
  step2_incremental_revenue_loss: number;
  final_claim_amount: number;
  notes?: string[];
}

export default function ClaimBreakdown({ step1_variable_profit_loss, step2_incremental_revenue_loss, final_claim_amount, notes }: ClaimBreakdownProps) {
  const formatCurrency = (val: number) => `$${val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div className="bg-gradient-to-br from-blue-50 to-blue-50 rounded-lg border-2 border-blue-200 p-6">
      <h3 className="text-xl font-bold text-gray-900 mb-4">BI Claim Calculation</h3>
      
      <div className="space-y-4">
        {/* Step 1 */}
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <div className="flex items-start justify-between mb-2">
            <div>
              <div className="flex items-center gap-2">
                <span className="bg-blue-600 text-white text-xs font-bold px-2 py-1 rounded">STEP 1</span>
                <h4 className="font-semibold text-gray-900">Variable Profit Loss</h4>
              </div>
              <p className="text-sm text-gray-600 mt-1">Decline in gross receipts during covered period</p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-blue-600">{formatCurrency(step1_variable_profit_loss)}</p>
            </div>
          </div>
        </div>

        {/* Step 2 */}
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <div className="flex items-start justify-between mb-2">
            <div>
              <div className="flex items-center gap-2">
                <span className="bg-blue-600 text-white text-xs font-bold px-2 py-1 rounded">STEP 2</span>
                <h4 className="font-semibold text-gray-900">Incremental Revenue Loss</h4>
              </div>
              <p className="text-sm text-gray-600 mt-1">Additional loss beyond benchmark period</p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-blue-600">{formatCurrency(step2_incremental_revenue_loss)}</p>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t-2 border-dashed border-gray-300 my-4"></div>

        {/* Final Amount */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-600 rounded-lg p-5 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm opacity-90 mb-1">FINAL CLAIM AMOUNT</p>
              <h3 className="text-4xl font-bold">{formatCurrency(final_claim_amount)}</h3>
            </div>
            <div className="text-6xl opacity-50">✓</div>
          </div>
        </div>
      </div>

      {/* Notes */}
      {notes && notes.length > 0 && (
        <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
          <p className="text-xs font-semibold text-yellow-900 mb-1">Calculation Notes:</p>
          <ul className="text-xs text-yellow-800 space-y-1">
            {notes.map((note, idx) => (
              <li key={idx}>• {note}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-4 text-xs text-gray-500 text-center">
        Calculation based on BI methodology using 48 expense categories and benchmark analysis
      </div>
    </div>
  );
}
